import React from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, ClockIcon, PhoneIcon, MailIcon } from 'lucide-react';
export function Location() {
  return <section id="location" className="bg-eucalyptus/30 py-24 md:py-32" data-id="element-108">
      <div className="max-w-6xl mx-auto px-6" data-id="element-109">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-110">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-medium text-botanical-text mb-4" data-id="element-111">
            Visit Our Studio
          </h2>
          <p className="font-sans text-botanical-muted max-w-xl mx-auto text-lg" data-id="element-112">
            Nestled in a peaceful corner of the city, our studio offers a
            tranquil escape from the everyday.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12" data-id="element-113">
          {/* Map placeholder / Location visual */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} className="bg-cream rounded-3xl overflow-hidden shadow-botanical-lg aspect-square lg:aspect-auto lg:min-h-[400px] relative" data-id="element-114">
            {/* Stylized map illustration */}
            <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full" aria-label="Decorative map illustration showing studio location" data-id="element-115">
              {/* Background */}
              <rect width="400" height="400" fill="#FAF8F3" data-id="element-116" />

              {/* Streets */}
              <path d="M0 200 L400 200" stroke="#A8B5A0" strokeWidth="8" strokeOpacity="0.3" data-id="element-117" />
              <path d="M200 0 L200 400" stroke="#A8B5A0" strokeWidth="8" strokeOpacity="0.3" data-id="element-118" />
              <path d="M0 100 L400 100" stroke="#A8B5A0" strokeWidth="4" strokeOpacity="0.2" data-id="element-119" />
              <path d="M0 300 L400 300" stroke="#A8B5A0" strokeWidth="4" strokeOpacity="0.2" data-id="element-120" />
              <path d="M100 0 L100 400" stroke="#A8B5A0" strokeWidth="4" strokeOpacity="0.2" data-id="element-121" />
              <path d="M300 0 L300 400" stroke="#A8B5A0" strokeWidth="4" strokeOpacity="0.2" data-id="element-122" />

              {/* Park/green space */}
              <ellipse cx="320" cy="80" rx="60" ry="50" fill="#8B9D83" fillOpacity="0.15" data-id="element-123" />
              <ellipse cx="80" cy="320" rx="50" ry="40" fill="#8B9D83" fillOpacity="0.15" data-id="element-124" />

              {/* Buildings */}
              <rect x="120" y="120" width="30" height="30" fill="#A8B5A0" fillOpacity="0.3" rx="4" data-id="element-125" />
              <rect x="250" y="220" width="40" height="35" fill="#A8B5A0" fillOpacity="0.3" rx="4" data-id="element-126" />
              <rect x="130" y="260" width="35" height="25" fill="#A8B5A0" fillOpacity="0.3" rx="4" data-id="element-127" />

              {/* Location marker */}
              <g transform="translate(200, 180)" data-id="element-128">
                <circle cx="0" cy="0" r="30" fill="#6B7A5E" fillOpacity="0.2" data-id="element-129" />
                <circle cx="0" cy="0" r="20" fill="#6B7A5E" fillOpacity="0.3" data-id="element-130" />
                <circle cx="0" cy="0" r="8" fill="#6B7A5E" data-id="element-131" />
              </g>

              {/* Small trees */}
              <circle cx="330" cy="70" r="8" fill="#8B9D83" fillOpacity="0.4" data-id="element-132" />
              <circle cx="310" cy="90" r="6" fill="#8B9D83" fillOpacity="0.35" data-id="element-133" />
              <circle cx="340" cy="95" r="5" fill="#8B9D83" fillOpacity="0.3" data-id="element-134" />
              <circle cx="70" cy="310" r="7" fill="#8B9D83" fillOpacity="0.4" data-id="element-135" />
              <circle cx="90" cy="330" r="5" fill="#8B9D83" fillOpacity="0.35" data-id="element-136" />
            </svg>

            {/* Label */}
            <div className="absolute bottom-6 left-6 bg-olive text-cream px-4 py-2 rounded-full font-sans text-sm" data-id="element-137">
              Our Studio
            </div>
          </motion.div>

          {/* Contact information */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} className="flex flex-col justify-center" data-id="element-138">
            <div className="space-y-8" data-id="element-139">
              {/* Address */}
              <div className="flex gap-4" data-id="element-140">
                <div className="flex-shrink-0 w-12 h-12 bg-sage/20 rounded-full flex items-center justify-center" data-id="element-141">
                  <MapPinIcon className="w-5 h-5 text-sage-dark" aria-hidden="true" data-id="element-142" />
                </div>
                <div data-id="element-143">
                  <h3 className="font-serif text-xl font-medium text-botanical-text mb-1" data-id="element-144">
                    Address
                  </h3>
                  <p className="font-sans text-botanical-muted" data-id="element-145">
                    Gartenstraße 42
                    <br data-id="element-146" />
                    10115 Berlin, Germany
                  </p>
                </div>
              </div>

              {/* Hours */}
              <div className="flex gap-4" data-id="element-147">
                <div className="flex-shrink-0 w-12 h-12 bg-sage/20 rounded-full flex items-center justify-center" data-id="element-148">
                  <ClockIcon className="w-5 h-5 text-sage-dark" aria-hidden="true" data-id="element-149" />
                </div>
                <div data-id="element-150">
                  <h3 className="font-serif text-xl font-medium text-botanical-text mb-1" data-id="element-151">
                    Hours
                  </h3>
                  <div className="font-sans text-botanical-muted space-y-1" data-id="element-152">
                    <p data-id="element-153">Monday – Friday: 9:00 – 19:00</p>
                    <p data-id="element-154">Saturday: 10:00 – 17:00</p>
                    <p data-id="element-155">Sunday: Closed</p>
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div className="flex gap-4" data-id="element-156">
                <div className="flex-shrink-0 w-12 h-12 bg-sage/20 rounded-full flex items-center justify-center" data-id="element-157">
                  <PhoneIcon className="w-5 h-5 text-sage-dark" aria-hidden="true" data-id="element-158" />
                </div>
                <div data-id="element-159">
                  <h3 className="font-serif text-xl font-medium text-botanical-text mb-1" data-id="element-160">
                    Phone
                  </h3>
                  <a href="tel:+4930123456789" className="font-sans text-botanical-muted hover:text-olive transition-colors" data-id="element-161">
                    +49 30 123 456 789
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex gap-4" data-id="element-162">
                <div className="flex-shrink-0 w-12 h-12 bg-sage/20 rounded-full flex items-center justify-center" data-id="element-163">
                  <MailIcon className="w-5 h-5 text-sage-dark" aria-hidden="true" data-id="element-164" />
                </div>
                <div data-id="element-165">
                  <h3 className="font-serif text-xl font-medium text-botanical-text mb-1" data-id="element-166">
                    Email
                  </h3>
                  <a href="mailto:hello@botanicalwellness.de" className="font-sans text-botanical-muted hover:text-olive transition-colors" data-id="element-167">
                    hello@botanicalwellness.de
                  </a>
                </div>
              </div>
            </div>

            {/* CTA */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} viewport={{
            once: true
          }} className="mt-10" data-id="element-168">
              <a href="mailto:hello@botanicalwellness.de?subject=Booking%20Inquiry" className="inline-flex items-center justify-center px-8 py-4 bg-olive text-cream font-sans font-medium rounded-full hover:bg-olive-dark transition-colors duration-300 shadow-botanical" data-id="element-169">
                Book Your Session
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}